// @flow
import type { User, FetchCurrentUserResponse, UtmParams } from '@rover/react-lib/types';
import fetchCurrentUserResponseFactory from '@rover/react-lib/factories/fetchCurrentUserResponseFactory';
import { createDuck } from '@rover/react-lib/lib/redux-duck';
import type {
  ActionCreator,
  EffectCreator,
  SelectorCreator,
  DuckOptions,
  Duck,
} from '@rover/react-lib/lib/redux-duck';
import { get } from '@rover/react-lib/apis/currentUser.api';
import { configureScope } from '@rover/utilities/sentry';
import reportError from '@rover/utilities/reportError';
import retry from '@rover/utilities/retry';

import generateActions from './generateActions';

export type State = {
  errorMessage: string,
  loading: boolean,
  logoutPending: boolean,
  fetchCurrentUserResponse: FetchCurrentUserResponse,
};

type Actions = {
  fetchCurrentUserPending: ActionCreator<void>,
  fetchCurrentUserSuccess: ActionCreator<FetchCurrentUserResponse>,
  fetchCurrentUserFailure: ActionCreator<string>,
  logoutCurrentUserPending: ActionCreator<void>,
  logoutCurrentUserSuccess: ActionCreator<FetchCurrentUserResponse>,
  logoutCurrentUserFailure: ActionCreator<string>,
};

type Effects = {
  fetchCurrentUser: EffectCreator<void>,
  logoutCurrentUser: EffectCreator<{ nextLocation: string, ref?: string }>,
  initSentryUser: EffectCreator<void>,
};

type Selectors = {
  getCurrentUser: SelectorCreator<User>,
  getRealUser: SelectorCreator<User>,
  isLoading: SelectorCreator<boolean>,
  getErrorMessage: SelectorCreator<string>,
  getApiUrl: SelectorCreator<string>,
  getOpk: SelectorCreator<string>,
  isAuthenticated: SelectorCreator<boolean>,
  isImpersonating: SelectorCreator<boolean>,
  getPeopleUrl: SelectorCreator<string>,
  isNewCustomer: SelectorCreator<boolean>,
  getPostalCode: SelectorCreator<string>,
  getUtmParams: SelectorCreator<UtmParams>,
  getFirstHttpReferrer: SelectorCreator<string>,
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'currentUser',
  initialState: {
    errorMessage: '',
    loading: true,
    logoutPending: false,
    fetchCurrentUserResponse: fetchCurrentUserResponseFactory(),
  },
  // $FlowFixMe
  actions: {
    ...generateActions('fetchCurrentUser'),
    ...generateActions('logoutCurrentUser'),
  },
  effects: {
    fetchCurrentUser: () => async (
      dispatch,
      getState,
      { fetchCurrentUserPending, fetchCurrentUserSuccess, fetchCurrentUserFailure },
      { initSentryUser },
      selectors,
      { expressParams }
    ) => {
      dispatch(fetchCurrentUserPending());
      try {
        const fetchCurrentUserResponse: FetchCurrentUserResponse = await get(expressParams);
        dispatch(fetchCurrentUserSuccess(fetchCurrentUserResponse));
        dispatch(initSentryUser());
      } catch (e) {
        if (e.status === 404) {
          dispatch(fetchCurrentUserSuccess(fetchCurrentUserResponseFactory()));
        } else {
          dispatch(fetchCurrentUserFailure(e.message));
          reportError(e);
        }
      }
    },
    logoutCurrentUser: ({ nextLocation, ref }) => async (
      // Only call this on the client, never the server
      dispatch,
      getState,
      { logoutCurrentUserPending, logoutCurrentUserSuccess, logoutCurrentUserFailure }
    ) => {
      dispatch(logoutCurrentUserPending());

      try {
        // These libraries depend on `window`, so we use dynamic import to avoid Webpack compilation issues
        const localStorage = await retry(() =>
          import(/* webpackChunkName: "localStorageAPI" */ '@rover/react-lib/apis/localStorage.api')
        );
        const sessionStorage = await retry(() =>
          import(
            /* webpackChunkName: "sessionStorageAPI" */ '@rover/react-lib/apis/sessionStorage.api'
          )
        );
        localStorage.clear();
        sessionStorage.clear();
        dispatch(logoutCurrentUserSuccess(fetchCurrentUserResponseFactory()));
        const encodedLocation = encodeURIComponent(nextLocation);
        window.location.href = `/logout/?next=${encodedLocation}${ref ? `&${ref}` : ''}`; // This is the actual logout
      } catch (e) {
        dispatch(logoutCurrentUserFailure(e.message));
        reportError(e);
      }
    },
    initSentryUser: () => async (dispatch, getState, actions, effects, { getOpk }) => {
      configureScope(scope => scope.setUser({ id: getOpk(getState()) }));
    },
  },
  selectors: {
    getCurrentUser: (getState, createSelector) =>
      createSelector(
        [getState],
        x => x.fetchCurrentUserResponse.currentUser
      ),
    getRealUser: (getState, createSelector) =>
      createSelector(
        [getState],
        x => x.fetchCurrentUserResponse.realUser
      ),
    isLoading: (getState, createSelector) =>
      createSelector(
        [getState],
        x => x.loading
      ),
    getErrorMessage: (getState, createSelector) =>
      createSelector(
        [getState],
        x => x.errorMessage
      ),
    getApiUrl: (getState, createSelector, { getCurrentUser }) =>
      createSelector(
        [getCurrentUser],
        x => x.apiUrl
      ),
    getOpk: (getState, createSelector, { getCurrentUser }) =>
      createSelector(
        [getCurrentUser],
        x => x.opk
      ),
    isAuthenticated: (getState, createSelector) =>
      createSelector(
        [getState],
        x => x.fetchCurrentUserResponse.authenticated
      ),
    isImpersonating: (getState, createSelector, { getRealUser }) =>
      createSelector(
        [getRealUser],
        x => !!x.opk.length
      ),
    getPeopleUrl: (getState, createSelector, { getCurrentUser }) =>
      createSelector(
        [getCurrentUser],
        x => `/admin/people/person/?slug=${x.slug}`
      ),
    isNewCustomer: (getState, createSelector, { getCurrentUser }) =>
      createSelector(
        [getCurrentUser],
        x => x.isNewCustomer
      ),
    getPostalCode: (getState, createSelector, { getCurrentUser }) =>
      createSelector(
        [getCurrentUser],
        x => x.postalCode
      ),
    getUtmParams: (getState, createSelector, { getCurrentUser }) =>
      createSelector(
        [getCurrentUser],
        x => x.session && x.session.utmParameters
      ),
    getFirstHttpReferrer: (getState, createSelector, { getCurrentUser }) =>
      createSelector(
        [getCurrentUser],
        x => x.session && x.session.firstHttpReferrer
      ),
  },
};

export const { actions, selectors, effects, reducer } = (createDuck(options): Duck<
  State,
  Actions,
  Effects,
  Selectors
>);



// WEBPACK FOOTER //
// ../react-lib/src/redux/ducks/currentUser.duck.js